import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const pageKey = new PluginKey("pageBreaks");
const fnKey = new PluginKey("footnoteMarks");
const FN_REF = /\[\^[\w-]+\]/g;

const fnDecos = (doc) => {
  const decos = [];
  doc.forEach((node, offset) => {
    if (node.type.name === "paragraph" && /^\[\^[\w-]+\]:/.test(node.textContent)) decos.push(Decoration.node(offset, offset + node.nodeSize, { class: "lf-fndef" }));
  });
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const m of node.text.matchAll(FN_REF)) decos.push(Decoration.inline(pos + m.index, pos + m.index + m[0].length, { class: "lf-fnref" }));
  });
  return DecorationSet.create(doc, decos);
};

export const FootnoteMarks = Extension.create({
  name: "footnoteMarks",
  addProseMirrorPlugins() {
    return [new Plugin({ key: fnKey, state: { init: (_c, s) => fnDecos(s.doc), apply: (tr, old) => (tr.docChanged ? fnDecos(tr.doc) : old) }, props: { decorations: (s) => fnKey.getState(s) } })];
  },
});

const spacer = (h, inline) => () => {
  const el = document.createElement(inline ? "span" : "div");
  el.className = "lf-pgap";
  el.contentEditable = "false";
  el.style.height = `${h}px`;
  return el;
};

const build = (doc, gaps) => DecorationSet.create(doc, gaps.filter((g) => g.pos <= doc.content.size)
  .map((g) => Decoration.widget(g.pos, spacer(g.h, g.inline), { side: -1, ignoreSelection: true, key: `pg-${g.pos}-${g.h}-${g.inline ? 1 : 0}` })));

export const PageBreaks = Extension.create({
  name: "pageBreaks",
  addProseMirrorPlugins() {
    return [new Plugin({
      key: pageKey,
      state: {
        init: () => ({ gaps: [], deco: DecorationSet.empty }),
        apply(tr, old, _o, state) {
          const gaps = tr.getMeta(pageKey);
          return gaps ? { gaps, deco: build(state.doc, gaps) } : { gaps: old.gaps, deco: old.deco.map(tr.mapping, tr.doc) };
        },
      },
      props: { decorations: (state) => pageKey.getState(state).deco },
    })];
  },
});

// Visual lines of a block, grouped from its text-node rects (client coords).
const linesOf = (dom) => {
  const lines = [];
  const walk = document.createTreeWalker(dom, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let t = walk.nextNode(); t; t = walk.nextNode()) {
    if (!t.textContent.length || t.parentElement?.closest(".lf-pgap")) continue;
    range.selectNodeContents(t);
    for (const r of range.getClientRects()) {
      if (r.height < 1) continue;
      const cur = lines[lines.length - 1];
      if (cur && r.top < (cur.top + cur.bottom) / 2) { cur.bottom = Math.max(cur.bottom, r.bottom); cur.left = Math.min(cur.left, r.left); }
      else lines.push({ top: r.top, bottom: r.bottom, left: r.left });
    }
  }
  return lines;
};

const ATOMIC = new Set(["table", "image", "horizontalRule"]);
const recent = new WeakMap();

// Keeps every line inside a page's text area: whole blocks or individual lines move to the next sheet.
export const paginate = (view, { contentH, stride, margin }) => {
  const origin = view.dom.getBoundingClientRect().top + margin;
  const existing = [...view.dom.querySelectorAll(".lf-pgap")].map((el) => { const r = el.getBoundingClientRect(); return { top: r.top, h: r.height }; });
  const natural = (y) => y - origin - existing.reduce((s, g) => (g.top <= y ? s + g.h : s), 0);
  const gaps = [];
  let shift = 0, bottom = 0;
  const pageEnd = (top) => Math.floor(top / stride) * stride + contentH;
  const nextPage = (top) => (Math.floor(top / stride) + 1) * stride;
  view.state.doc.forEach((node, offset) => {
    const dom = view.nodeDOM(offset);
    if (!(dom instanceof HTMLElement)) return;
    const r = dom.getBoundingClientRect();
    const top = natural(r.top) + shift;
    const lines = ATOMIC.has(node.type.name) ? [] : linesOf(dom);
    if (!lines.length) {
      const h = r.height;
      if (top >= pageEnd(top) || (top + h > pageEnd(top) && h <= contentH && top > Math.floor(top / stride) * stride + 1)) {
        const e = Math.round(nextPage(top) - top);
        gaps.push({ pos: offset, h: e });
        shift += e;
      }
      bottom = natural(r.bottom) + shift;
      return;
    }
    lines.forEach((ln, i) => {
      const lt = natural(ln.top) + shift;
      const lb = natural(ln.bottom) + shift;
      if (lb <= pageEnd(lt) + 0.5 && lt < pageEnd(lt)) return;
      if (i === 0) {
        const bt = Math.min(top, lt);
        const e = Math.round(nextPage(lt) - bt);
        if (offset > 0 || bt > 1) { gaps.push({ pos: offset, h: e }); shift += e; }
        return;
      }
      const hit = view.posAtCoords({ left: ln.left + 1, top: (ln.top + ln.bottom) / 2 });
      if (!hit || hit.pos <= offset + 1 || hit.pos >= offset + node.nodeSize - 1) return;
      const e = Math.round(nextPage(lt) - lt);
      gaps.push({ pos: hit.pos, h: e, inline: true });
      shift += e;
    });
    bottom = natural(r.bottom) + shift;
  });
  const next = JSON.stringify(gaps), cur = JSON.stringify(pageKey.getState(view.state).gaps);
  const h = recent.get(view) || { doc: null, key: "", seen: [] };
  const key = `${contentH}:${stride}:${view.dom.offsetWidth}`;
  if (h.doc !== view.state.doc || h.key !== key) { h.doc = view.state.doc; h.key = key; h.seen = []; }
  // Stop A→B→A loops: a layout already tried for this exact text is not re-applied.
  if (next !== cur && !h.seen.includes(next)) {
    h.seen.push(cur);
    recent.set(view, h);
    view.dispatch(view.state.tr.setMeta(pageKey, gaps).setMeta("addToHistory", false));
  }
  return Math.max(1, Math.floor(Math.max(0, bottom - 1) / stride) + 1);
};
